import {
    DurationUnit,
    addDays,
    endOfDay,
    format,
    getTime,
    isAfter,
    isBefore,
    isSameDay,
    isValid,
    parseISO,
    roundToNearestMinutes,
    sub,
    subDays,
    subMinutes,
} from 'date-fns';
import { isNil, max } from 'lodash';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useDisplayPreferences } from '@/api/userAccount';
import { Timeframe } from '@/components/spotter/notificationTab/TimeframeButtonGroup';
import { DEFAULT_HISTORICAL_DATA_DATE_RANGE_DISTANCE_DAYS, MAX_HISTORICAL_DATA_DATE_RANGE_DAYS } from '@/config';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useNow } from '@/hooks/useNow';
import { useTzContext } from '@/hooks/useTzContext';
import { DateRange } from '@/store';
import { clampRangeToMaxSpan } from '@/utils';

const START_DATE_URL_PARAM = 'startDate';
const END_DATE_URL_PARAM = 'endDate';

export type DateRangeContext = {
    /**
     * Date stamp of the form 'yyyy-MM-dd'
     */
    startDateStamp: string | null;
    /**
     * Date stamp of the form 'yyyy-MM-dd'
     */
    endDateStamp: string | null;
    /**
     * Timestamp of the first millisecond of the start date (in the user's preferred TZ)
     */
    startTs: number | null;
    /**
     * Timestamp of the last millisecond of the end date, or now if end date is today (in the user's preferred TZ).
     */
    endTs: number | null;
    /**
     * startTs, endTs, as a pair of Dates
     */
    dateRange: DateRange | null;

    isTrackingLive: boolean;

    setDateRange: (startDate: string | Date | null, endDate: string | Date | null) => void;
    setTimeframe: (value: number, unit: DurationUnit) => void;
    setTrackingLive: (live: boolean) => void;
    setMaxObservedTimestamp: (timestamp: string | Date | null) => void;

    isMutable: boolean;
};

/**
 * No provider means there is no range to speak of: nothing to read, and nothing that could accept a
 * change. The setters are inert rather than absent so a consumer outside a provider is a no-op instead of
 * a crash, and `isMutable` stays the way to ask whether a change would land anywhere.
 */
const NO_DATE_RANGE_CONTEXT: DateRangeContext = {
    startDateStamp: null,
    endDateStamp: null,
    startTs: null,
    endTs: null,
    dateRange: null,
    isTrackingLive: false,
    setDateRange: () => {},
    setTimeframe: () => {},
    setTrackingLive: () => {},
    setMaxObservedTimestamp: () => {},
    isMutable: false,
};

const DateRangeContext = createContext(NO_DATE_RANGE_CONTEXT);

/**
 * Manages date range selection with URL synchronization.
 *
 * URL Parameters:
 * - startDate: optional, ISO date string (YYYY-MM-DD)
 * - endDate: optional, ISO date string (YYYY-MM-DD)
 *
 * @example
 * // URL: /spotter/123?startDate=2023-12-01&endDate=2023-12-31
 */
export const DateRangeContextProvider = ({ children }: { children: ReactNode }) => {
    const dateFnCtx = useTzContext();
    const { isPending: tzPending } = useDisplayPreferences();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [startDateStamp, setStartDateStamp] = useState<string | null>(null);
    const [endDateStamp, setEndDateStamp] = useState<string | null>(null);
    const [maxObservedTs, setMaxObservedTs] = useState<number | null>(null);
    const now = useNow(300);
    const { setSelectedSubsampleTimestamp, setSelectedTimestamp, selectedTimestamp, selectedSubsampleTimestamp } =
        useTimelineNavigationContext();

    const startTs = useMemo(() => {
        if (!startDateStamp) return null;

        return parseISO(startDateStamp, dateFnCtx).getTime();
    }, [startDateStamp, dateFnCtx]);

    const endTs = useMemo(() => {
        if (!endDateStamp) return null;

        const endTs = endOfDay(parseISO(endDateStamp, dateFnCtx), dateFnCtx).getTime();
        const today = isSameDay(now, endTs, dateFnCtx);
        if (today) {
            if (maxObservedTs && isSameDay(maxObservedTs, endTs, dateFnCtx) && maxObservedTs > now.getTime()) {
                // if we saw an observation more recent than our (up to 5 minute delayed) now, and it's the same day as endTs (e.g. not corrupt data from the future), use that instead of now
                return maxObservedTs;
            }
            return now.getTime();
        }
        return endTs;
    }, [endDateStamp, dateFnCtx, now, maxObservedTs]);

    const dateRange = useMemo(() => {
        if (tzPending || isNil(startTs) || isNil(endTs)) {
            return null;
        }

        return { startDate: new Date(startTs), endDate: new Date(endTs) };
    }, [tzPending, startTs, endTs]);

    const setValidatedDateRange = useCallback(
        (startDate: Date, endDate: Date) => {
            /*
             * Every way a range can be set arrives here - the picker, the URL params, `setTimeframe` - so
             * this is where the maximum span is enforced. The end is the edge that is kept, because it is
             * the one the charts are showing. The day arithmetic runs in the preferred timezone, the same
             * one the stamps below are formatted in.
             */
            const clamped = clampRangeToMaxSpan(
                { startDate, endDate },
                MAX_HISTORICAL_DATA_DATE_RANGE_DAYS,
                'end',
                dateFnCtx
            );

            const start = format(clamped.startDate, 'yyyy-MM-dd', dateFnCtx);
            const end = format(clamped.endDate, 'yyyy-MM-dd', dateFnCtx);
            setStartDateStamp(start);
            setEndDateStamp(end);

            const oldStart = searchParams.get(START_DATE_URL_PARAM);
            const oldEnd = searchParams.get(END_DATE_URL_PARAM);
            if (start === oldStart && end === oldEnd) {
                // don't set search params if they haven't changed (avoids extra history entries)
                return;
            }

            // if this is the first time setting searchParams, don't treat it as a navigation so we avoid a redundant back
            const replace = !(oldStart && oldEnd);

            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set(START_DATE_URL_PARAM, start);
            newSearchParams.set(END_DATE_URL_PARAM, end);
            navigate('?' + newSearchParams.toString() + (location.hash ?? ''), { replace, state: location.state });
        },
        [dateFnCtx, location.hash, location.state, navigate, searchParams]
    );

    /*
     * The default range below wants the present, but not reactively: closing over `now` would rebuild this
     * callback every five minutes, and it is a dependency of the context value that every chart and every
     * date-ranged query subscribes to. On a settled range nothing else in that value moves, so the rebuild
     * alone is what wakes the whole page twelve times an hour.
     */
    const nowRef = useRef(now);
    nowRef.current = now;

    const setDateRange = useCallback(
        (startDate: Date | string | null, endDate: Date | string | null) => {
            const start = startDate instanceof Date ? startDate : parseISO(startDate || '', dateFnCtx);
            const end = endDate instanceof Date ? endDate : parseISO(endDate || '', dateFnCtx);

            // !isAfter rather than isBefore so we allow single-day ranges
            if (isValid(start) && isValid(end) && !isAfter(start, end)) {
                setValidatedDateRange(start, end);
            } else if (isValid(end)) {
                setValidatedDateRange(subDays(end, DEFAULT_HISTORICAL_DATA_DATE_RANGE_DISTANCE_DAYS), end);
            } else if (isValid(start)) {
                setValidatedDateRange(start, addDays(start, DEFAULT_HISTORICAL_DATA_DATE_RANGE_DISTANCE_DAYS));
            } else {
                const rightNow = nowRef.current;
                setValidatedDateRange(subDays(rightNow, DEFAULT_HISTORICAL_DATA_DATE_RANGE_DISTANCE_DAYS), rightNow);
            }
        },
        [dateFnCtx, setValidatedDateRange]
    );

    const setTimeframe = useCallback(
        (value: number, unit: DurationUnit) => {
            if (!endTs) {
                console.error('Unable to set time span on date range context without an end date');
                return;
            }

            const duration = { [unit]: value };
            setValidatedDateRange(sub(endTs, duration), new Date(endTs));
        },
        [endTs, setValidatedDateRange]
    );

    const setMaxObservedTimestamp = useCallback(
        (timestamp: string | Date | null) => {
            if (!timestamp) return;

            const ts = timestamp instanceof Date ? timestamp : parseISO(timestamp || '', dateFnCtx);
            setMaxObservedTs((oldTs) => max([ts.getTime(), oldTs]) ?? null);
        },
        [dateFnCtx]
    );

    useEffect(() => {
        const startDateParam = searchParams.get(START_DATE_URL_PARAM);
        const endDateParam = searchParams.get(END_DATE_URL_PARAM);

        if (startDateStamp && endDateStamp) {
            // assuming dates are already set, ignore query params...
            if (!startDateParam || !endDateParam) {
                // ...if they're not present (avoids overwriting explicitly set value with defaulted params)
                return;
            }
            if (startDateStamp === startDateParam && endDateStamp === endDateParam) {
                // ...or if there's no change (avoid unnecessary renders if e.g. we're tracking 'now')
                return;
            }
        }

        setDateRange(startDateParam, endDateParam);
    }, [endDateStamp, searchParams, setDateRange, startDateStamp]);

    /**
     * Effect: If the date range doesn't include the current timestamp cursor, clear it.
     */
    useEffect(() => {
        if (!dateRange) {
            return;
        }

        if (
            selectedTimestamp &&
            (isBefore(selectedTimestamp, dateRange.startDate) || isAfter(selectedTimestamp, dateRange.endDate))
        ) {
            setSelectedTimestamp(null);
        }
        if (
            selectedSubsampleTimestamp &&
            (isBefore(selectedSubsampleTimestamp, dateRange.startDate) ||
                isAfter(selectedSubsampleTimestamp, dateRange.endDate))
        ) {
            setSelectedSubsampleTimestamp(null);
        }
    }, [dateRange, selectedSubsampleTimestamp, selectedTimestamp, setSelectedSubsampleTimestamp, setSelectedTimestamp]);

    const contextValue: DateRangeContext = useMemo(
        () => ({
            startDateStamp,
            endDateStamp,
            startTs,
            endTs,
            dateRange,
            setDateRange,
            setTimeframe,
            setMaxObservedTimestamp,
            setTrackingLive: () => {
                throw new Error('Unsupported setTrackingLive');
            },
            isMutable: true,
            isTrackingLive: false,
        }),
        [startDateStamp, endDateStamp, startTs, endTs, dateRange, setDateRange, setTimeframe, setMaxObservedTimestamp]
    );

    return <DateRangeContext.Provider value={contextValue}>{children}</DateRangeContext.Provider>;
};

export const FixedDateRangeContextProvider = ({
    startDate,
    endDate,
    children,
}: {
    children: ReactNode;
    startDate: Date;
    endDate: Date;
}) => {
    const tz = useTzContext();

    const contextValue = useMemo(
        () => ({
            startTs: startDate.getTime(),
            endTs: endDate.getTime(),
            startDateStamp: format(startDate, 'yyyy-MM-dd', tz),
            endDateStamp: format(endDate, 'yyyy-MM-dd', tz),
            dateRange: { startDate, endDate },
            isTrackingLive: false,
            setDateRange: () => {
                throw new Error('Unsupported setDateRange');
            },
            setTimeframe: () => {
                throw new Error('Unsupported setTimeSpan');
            },
            setTrackingLive: () => {
                throw new Error('Unsupported setTrackingLive');
            },
            setMaxObservedTimestamp: () => {
                throw new Error('Unsupported setMaxObservedTimestamp');
            },
            isMutable: false,
        }),
        [endDate, startDate, tz]
    );

    return <DateRangeContext.Provider value={contextValue}>{children}</DateRangeContext.Provider>;
};

export const RecentDateTimeRangeContextProvider = ({ children }: { children: ReactNode }) => {
    const tz = useTzContext();
    const [maxObservedTs, setMaxObservedTs] = useState<number | null>(null);
    const now = useNow(300);
    const [timeframe, setTimeframe] = useState<Timeframe>([6, 'hours']);
    const [isTrackingLive, setTrackingLive] = useState(true);
    const lastLiveEndTs = useRef(0);

    const { setSelectedSubsampleTimestamp, setSelectedTimestamp, selectedTimestamp, selectedSubsampleTimestamp } =
        useTimelineNavigationContext();

    const setMaxObservedTimestamp = useCallback(
        (timestamp: string | Date | null) => {
            if (!timestamp) return;

            const ts = timestamp instanceof Date ? timestamp : parseISO(timestamp || '', tz);
            setMaxObservedTs((oldTs) => max([ts.getTime(), oldTs]) ?? null);
        },
        [tz]
    );

    const endTs = useMemo(() => {
        // if we saw an observation more recent than our (up to 5 minute delayed) now, and it's not too wildly in
        // the future (e.g. corrupt data), use that instead of now
        const preferMaxObserved =
            maxObservedTs && maxObservedTs > now.getTime() && maxObservedTs < addDays(now, 1).getTime();
        const rawEnd = preferMaxObserved ? maxObservedTs : now.getTime();

        // round up to the minute so that we don't redraw everything every second
        return roundToNearestMinutes(rawEnd, { roundingMethod: 'ceil' }).getTime();
    }, [maxObservedTs, now]);

    const startTs = useMemo(() => sub(endTs, { [timeframe[1]]: timeframe[0] }).getTime(), [timeframe, endTs]);
    const dateRange = useMemo(() => ({ startDate: new Date(startTs), endDate: new Date(endTs) }), [startTs, endTs]);

    /**
     * Effect: Manage selectedTimestamp with respect to isTrackingLive:
     * - If live, selected timestamp follows the end of the window (or if someone moves it explicitly, turn live off)
     * - If selected timestamp falls out of the window, or if someone explicitly sets it to the very end, turn live on
     * - If the selected timestamp got cleared, turn on live tracking
     */
    useEffect(() => {
        if (!selectedTimestamp) {
            setTrackingLive(true);
            lastLiveEndTs.current = endTs;
            const endTimestamp = new Date(endTs).toISOString();
            setSelectedTimestamp(endTimestamp);
            return;
        }
        const selectedTs = getTime(selectedTimestamp);
        if (isTrackingLive) {
            if (selectedTs < lastLiveEndTs.current) {
                // someone manually moved it away from the end, turn live off
                setTrackingLive(false);
            } else {
                lastLiveEndTs.current = endTs;
                const endTimestamp = new Date(endTs).toISOString();
                setSelectedTimestamp(endTimestamp);
                if (selectedSubsampleTimestamp) {
                    setSelectedSubsampleTimestamp(endTimestamp);
                }
            }
        } else {
            lastLiveEndTs.current = 0;
            if (selectedTs >= subMinutes(endTs, 1).getTime() || selectedTs < startTs) {
                // someone dragged it to within a minute of the end
                // - OR -
                // the selected timestamp fell out of the beginning of the time range (or the time range was narrowed to more recent)
                setTrackingLive(true);
            }
        }
        if (selectedSubsampleTimestamp && isBefore(selectedSubsampleTimestamp, startTs)) {
            setSelectedSubsampleTimestamp(null);
        }
    }, [
        dateRange,
        endTs,
        isTrackingLive,
        selectedSubsampleTimestamp,
        selectedTimestamp,
        setSelectedSubsampleTimestamp,
        setSelectedTimestamp,
        startTs,
    ]);

    const contextValue = useMemo(
        () => ({
            startTs,
            endTs,
            startDateStamp: format(startTs, 'yyyy-MM-dd', tz),
            endDateStamp: format(endTs, 'yyyy-MM-dd', tz),
            dateRange,
            isTrackingLive,
            setDateRange: () => {
                throw new Error('Unsupported setDateRange');
            },
            setTimeframe: (value: number, unit: DurationUnit) => {
                setTimeframe([value, unit]);
            },
            setTrackingLive: (live: boolean) => {
                setTrackingLive(live);
            },
            setMaxObservedTimestamp,
            isMutable: true,
        }),
        [dateRange, endTs, isTrackingLive, setMaxObservedTimestamp, startTs, tz]
    );

    return <DateRangeContext.Provider value={contextValue}>{children}</DateRangeContext.Provider>;
};

export const useDateRangeContext = () => useContext(DateRangeContext);
